import axios, { AxiosResponse } from 'axios';
import { CommentTypeId, IComment } from '../../../components/Comments/Comments.types';
import {
  AnnotationOnImage,
  AnnotationsCriterionDTO,
  AnnotationsForORGZ,
  AnnotationVersionsForORGZ,
} from '../../../models/annotation.types';
import {
  deleteScoringState,
  deleteSheetState,
  deleteState,
  ExamTaskGroupSolutionScoreShelfResponse,
  ExamTaskGroupSolutionWithTasks,
} from '../../../models/assessment.types';
import EnvWindow from '../../../utils/env';
import { transformResponsePreserveBigInts, isNonEmptyString } from '../../../utils/servicesHelper';
import { SendBackAssessedExamTaskGroupSolutionsResponse } from '../../scoring/scoring.types';

const annotationDeleteInFlightRequests = new Map<string, Promise<AxiosResponse<boolean>>>();

const deleteSingleAnnotation = (annotationId: string): Promise<AxiosResponse<boolean>> => {
  const pendingRequest = annotationDeleteInFlightRequests.get(annotationId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = axios
    .delete<boolean>(`${EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL}/${annotationId}`)
    .finally(() => {
      annotationDeleteInFlightRequests.delete(annotationId);
    });

  annotationDeleteInFlightRequests.set(annotationId, request);
  return request;
};

export async function getExamTaskGroup(): Promise<ExamTaskGroupSolutionWithTasks> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`);
  return response.data;
}

export async function deleteExamTaskGroup(examTaskGroupId: string | null): Promise<deleteState> {
  const response = await axios.delete(
    examTaskGroupId !== null
      ? `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/${examTaskGroupId}`
      : `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`,
  );
  return response.data;
}

export async function deleteExamScoringSchema(examScoringSchemaId: string | null): Promise<deleteScoringState> {
  const response = await axios.delete(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}`,
  );
  return response.data;
}

export async function deleteExamSheet(examSheetId: string | null): Promise<deleteSheetState> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}`);
  return response.data;
}

export async function deleteExamTask(examTaskId: string | null): Promise<deleteSheetState> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask/${examTaskId}`);
  return response.data;
}

export const getComments = async (
  orgzID: string,
  examTaskGroupSolutionScoreVersionId?: string,
): Promise<AxiosResponse<IComment[]>> => {
  const examTaskGroupSolutionScoreVersionIdPart = examTaskGroupSolutionScoreVersionId
    ? `?examTaskGroupSolutionScoreVersionId=${examTaskGroupSolutionScoreVersionId}`
    : '';
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${orgzID}/Comment${examTaskGroupSolutionScoreVersionIdPart}`,
  );
  return response;
};

export async function addComment(
  orgzID: string | undefined,
  commentTypeId: CommentTypeId,
  content: string,
  x1: number | null = null,
  y1: number | null = null,
  x2: number | null = null,
  y2: number | null = null,
  pageNumber: number | null = null,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const response = axios.put(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${orgzID}/Comment`,
    {
      commentTypeId,
      content,
      x1: x1 !== null ? Math.round(x1) : null,
      y1: y1 !== null ? Math.round(y1) : null,
      x2: x2 !== null ? Math.round(x2) : null,
      y2: y2 !== null ? Math.round(y2) : null,
      pageNumber,
      partialScoreId,
    },
  );
  return response;
}

export async function updateComment(
  commentId: string | undefined,
  content: string,
  x1: number | null = null,
  y1: number | null = null,
  x2: number | null = null,
  y2: number | null = null,
  pageNumber: number | null = null,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const response = axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/Comment/${commentId}`,
    {
      content,
      x1: x1 !== null ? Math.round(x1) : null,
      y1: y1 !== null ? Math.round(y1) : null,
      x2: x2 !== null ? Math.round(x2) : null,
      y2: y2 !== null ? Math.round(y2) : null,
      pageNumber,
      partialScoreId,
    },
  );
  return response;
}

export async function deleteComment(
  commentId: string,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const partialScoreIdPart = partialScoreId ? `?partialScoreId=${partialScoreId}` : '';
  const response = axios.delete(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/Comment/${commentId}${partialScoreIdPart}`,
  );
  return response;
}

export const getAnnotations = async (
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId: string | undefined,
): Promise<AxiosResponse<AnnotationsForORGZ>> => {
  const examTaskGroupSolutionScoreVersionIdPart = examTaskGroupSolutionScoreVersionId
    ? `?examTaskGroupSolutionScoreVersionId=${examTaskGroupSolutionScoreVersionId}`
    : '';
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/Annotations${examTaskGroupSolutionScoreVersionIdPart}`,
  );
  return response;
};

export const getAnnotationVersions = async (
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<AnnotationVersionsForORGZ>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/AnnotationVersions`,
  );
  return response;
};

export async function addAnnotationOnOrgz(
  partialScoreId: string,
  annotation: AnnotationOnImage,
): Promise<AxiosResponse<string>> {
  if (!isNonEmptyString(partialScoreId) || !isNonEmptyString(annotation.annotationDefinitionId)) {
    return Promise.reject(new Error('Missing required annotation identifiers.'));
  }

  const response = axios.post(
    EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL,
    {
      partialScoreId,
      annotationDefinitionId: annotation.annotationDefinitionId,
      positionX: Math.round(annotation.positionX),
      positionY: Math.round(annotation.positionY),
      pageNumber: annotation.pageNumber,
      width: annotation.width !== null ? Math.round(annotation.width) : null,
      height: annotation.height !== null ? Math.round(annotation.height) : null,
      angle: annotation.angle !== null ? Math.round(annotation.angle) : null,
      layer: annotation.layer,
    },
    {
      // transformes response to string in order to prevent returning it as number which is rounded
      transformResponse: [
        function (data: string) {
          return data;
        },
      ],
    },
  );
  return response;
}

export async function updateAnnotationOnOrgz(annotations: AnnotationOnImage[]): Promise<boolean> {
  const data = annotations
    .filter((annotation) => isNonEmptyString(annotation.annotationId))
    .map((annotation) => ({
      positionX: Math.round(annotation.positionX),
      positionY: Math.round(annotation.positionY),
      pageNumber: annotation.pageNumber,
      width: annotation.width !== null ? Math.round(annotation.width) : null,
      height: annotation.height !== null ? Math.round(annotation.height) : null,
      angle: annotation.angle !== null ? Math.round(annotation.angle) : null,
      layer: annotation.layer,
      id: annotation.annotationId,
    }));

  if (!data.length) {
    return true;
  }

  const response = await axios.patch<boolean>(EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL, data);
  return response.data;
}

export const getCriterionAnnotation = async (
  examScoringSchemaId: string,
  orgzId: string | undefined,
): Promise<AxiosResponse<AnnotationsCriterionDTO>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/ExamScoringSchema/${examScoringSchemaId}/ExaminerAnnotation/${orgzId}`,
  );
  return response;
};

export const setCriterionAnnotationSequence = async (
  examScoringSchemaId: string,
  sequence: string[] | undefined,
): Promise<AxiosResponse<AnnotationsCriterionDTO>> => {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/ExamScoringSchema/${examScoringSchemaId}/ExaminerAnnotation`,
    sequence,
  );
  return response;
};

export async function deleteAnnotationOnOrgz(annotationId: string | string[]): Promise<AxiosResponse<boolean>> {
  if (Array.isArray(annotationId)) {
    const response = await Promise.all(annotationId.map((id) => deleteSingleAnnotation(id)));
    return response[0];
  } else {
    const response = deleteSingleAnnotation(annotationId);
    return response;
  }
}

export const createExamTaskGroupSolutionScoreShelf = async (
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<ExamTaskGroupSolutionScoreShelfResponse>> => {
  const response = axios.post(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/shelf`,
    undefined,
    { transformResponse: [transformResponsePreserveBigInts] },
  );
  return response;
};

export async function sendBackAssessedSolutionsShelf(
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId: string,
): Promise<AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/latest/${examTaskGroupSolutionScoreVersionId}`,
  );
}
